import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext({ user: null, loading: true });

/**
 * AuthProvider — session + sign-in prompt state.
 *
 * `login()` now OPENS the shared SignInDialog (which lets the user
 * pick Google OR magic-link email). Callers that specifically want
 * the direct-to-Google flow (e.g. some marketing CTAs) can still
 * call `loginWithGoogle()`.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signInOpen, setSignInOpen] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      if (data && data.user_id) setUser(data); else setUser(null);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const loginWithGoogle = () => {
    // Remember where the user was so we can send them back after OAuth
    try { sessionStorage.setItem("wua_return_to", window.location.pathname + window.location.search); } catch {}
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Every existing `login()` caller in the app now opens the shared
  // SignInDialog. That means the magic-link email option is present
  // wherever a user might click "Sign in" — top nav, save-journey
  // prompts, dashboards, everywhere.
  const login = () => setSignInOpen(true);
  const openSignIn = () => setSignInOpen(true);
  const closeSignIn = () => setSignInOpen(false);

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  return (
    <AuthCtx.Provider
      value={{
        user,
        loading,
        login,               // opens shared dialog (Google + magic-link)
        loginWithGoogle,     // legacy direct-to-Google
        logout,
        refresh: checkAuth,
        signInOpen,
        openSignIn,
        closeSignIn,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
